import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import AuthGate from '../../components/AuthGate/AuthGate';
import authService from '../../services/auth.service';
import { useThemeColor } from '../../hooks/useThemeColor';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import PhoneInput from '../../components/PhoneInput/PhoneInput';

/**
 * Register Page – step 1: collect info and send OTP, then navigate to verification page.
 */

/** Map API error codes to French messages */
const API_ERROR_MESSAGES: Record<string, string> = {
  PHONE_ALREADY_EXISTS: 'Ce numéro de téléphone est déjà enregistré.',
  EMAIL_ALREADY_EXISTS: 'Cette adresse e-mail est déjà utilisée.',
  PSEUDO_ALREADY_EXISTS: 'Ce pseudo est déjà utilisé.',
  INVALID_OR_EXPIRED_OTP: 'Code invalide ou expiré. Demandez un nouveau code.',
  SMS_SEND_FAILED: 'Impossible d\'envoyer le code. Réessayez plus tard.',
  VALIDATION_ERROR: 'Vérifiez les champs du formulaire.',
  IP_RATE_LIMIT: 'Trop de demandes. Réessayez plus tard.',
  PHONE_RATE_LIMIT: 'Trop de codes envoyés à ce numéro. Réessayez dans une heure.',
};

function getApiErrorMessage(err: any): string {
  const code = err.response?.data?.error?.code;
  const fallback = err.response?.data?.error?.message || err.message;
  return (code && API_ERROR_MESSAGES[code]) || fallback;
}

interface PreviousFormState {
  previousFormData?: {
    email: string;
    password: string;
    confirmPassword: string;
    first_name: string;
    last_name: string;
    phone: string;
    pseudo: string;
  };
}

const Register: React.FC = () => {
  useThemeColor('#6B21A8');
  const navigate = useNavigate();
  const location = useLocation();
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [sendOtpError, setSendOtpError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    pseudo: '',
  });

  const [formErrors, setFormErrors] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    pseudo: '',
  });

  // Prefill when returning from "Change number" on verify page
  useEffect(() => {
    const state = location.state as PreviousFormState | null;
    if (state?.previousFormData) {
      setFormData(state.previousFormData);
    }
  }, [location.state]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (sendOtpError) setSendOtpError('');
  };

  const validateForm = (): boolean => {
    const email = formData.email.trim();
    const first_name = formData.first_name.trim();
    const last_name = formData.last_name.trim();
    const pseudo = formData.pseudo.trim();

    const errors = {
      email: '',
      password: '',
      confirmPassword: '',
      first_name: '',
      last_name: '',
      phone: '',
      pseudo: '',
    };

    if (!email) {
      errors.email = 'L\'email est obligatoire';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Format d\'email invalide';
    }

    if (!formData.password) {
      errors.password = 'Le mot de passe est obligatoire';
    } else if (formData.password.length < 8) {
      errors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Le mot de passe doit contenir une majuscule, une minuscule et un chiffre';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (!first_name) {
      errors.first_name = 'Le prénom est obligatoire';
    } else if (first_name.length < 2) {
      errors.first_name = 'Le prénom doit contenir au moins 2 caractères';
    }

    if (!last_name) {
      errors.last_name = 'Le nom est obligatoire';
    } else if (last_name.length < 2) {
      errors.last_name = 'Le nom doit contenir au moins 2 caractères';
    }

    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!formData.phone) {
      errors.phone = 'Le numéro de téléphone est obligatoire';
    } else if (phoneDigits.length < 10 || phoneDigits.length > 15) {
      errors.phone = 'Saisissez un numéro valide avec indicatif pays';
    }

    if (!pseudo) {
      errors.pseudo = 'Le pseudo est obligatoire';
    } else if (pseudo.length < 3) {
      errors.pseudo = 'Le pseudo doit contenir au moins 3 caractères';
    } else if (!/^[a-zA-Z0-9]+$/.test(pseudo)) {
      errors.pseudo = 'Le pseudo ne peut contenir que des lettres et des chiffres';
    }

    setFormErrors(errors);
    return Object.values(errors).every((e) => !e);
  };

  const handleSendOtp = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSendOtpError('');
    setSendOtpLoading(true);
    try {
      const phoneNormalized = formData.phone.replace(/\D/g, '');
      const otpResponse = await authService.sendOtp(phoneNormalized);
      navigate('/register/verify', {
        state: {
          formData: {
            email: formData.email.trim(),
            password: formData.password,
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            phone: formData.phone,
            pseudo: formData.pseudo.trim(),
          },
          phoneNormalized,
          smsNotDelivered: otpResponse.data?.smsDelivered === false,
        },
      });
    } catch (err: any) {
      setSendOtpError(getApiErrorMessage(err) || 'Impossible d\'envoyer le code. Réessayez.');
    } finally {
      setSendOtpLoading(false);
    }
  };

  return (
    <AuthGate
      panel={{
        eyebrow: 'Inscription',
        title: <>Un compte. <em>Un store.</em></>,
        lead: 'Statuts, champs, dashboard — ensuite.',
        points: ['File de confirmation', 'Envoi depuis la fiche', 'Rôles nets'],
      }}
    >
      <p className="auth-kicker">Compte</p>
      <h1 className="auth-h">Créer un compte</h1>
      <p className="auth-sub">Puis le code SMS.</p>
      <ol className="auth-steps">
        <li className="is-on">Infos</li>
        <li>Code</li>
      </ol>

      {sendOtpError && (
        <div className="auth-alert auth-alert--error" role="alert">
          {sendOtpError}
        </div>
      )}

      <form className="auth-form" onSubmit={handleSendOtp} noValidate>
        <div className="auth-row">
          <Input
            label="Prénom"
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            error={formErrors.first_name}
            placeholder="Jean"
            required
            fullWidth
            autoComplete="given-name"
          />
          <Input
            label="Nom"
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            error={formErrors.last_name}
            placeholder="Dupont"
            required
            fullWidth
            autoComplete="family-name"
          />
        </div>

        <Input
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          error={formErrors.email}
          placeholder="vous@exemple.com"
          required
          fullWidth
          autoComplete="email"
        />

        <PhoneInput
          label="Téléphone"
          value={formData.phone}
          onChange={(v) => {
            setFormData((prev) => ({ ...prev, phone: v }));
            if (formErrors.phone) setFormErrors((prev) => ({ ...prev, phone: '' }));
            if (sendOtpError) setSendOtpError('');
          }}
          error={formErrors.phone}
          placeholder="6 12 34 56 78"
          required
          fullWidth
        />

        <Input
          label="Pseudo"
          type="text"
          name="pseudo"
          value={formData.pseudo}
          onChange={handleChange}
          error={formErrors.pseudo}
          placeholder="monpseudo"
          required
          fullWidth
          autoComplete="username"
        />

        <Input
          label="Mot de passe"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          error={formErrors.password}
          placeholder="8 caractères min."
          required
          fullWidth
          autoComplete="new-password"
        />

        <Input
          label="Confirmer"
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={formErrors.confirmPassword}
          placeholder="Saisissez à nouveau"
          required
          fullWidth
          autoComplete="new-password"
        />

        <Button
          type="submit"
          variant="primary"
          size="large"
          fullWidth
          loading={sendOtpLoading}
        >
          Envoyer le code
        </Button>
      </form>

      <div className="auth-foot">
        <p>
          Déjà un compte ?{' '}
          <Link to="/login">Se connecter</Link>
        </p>
      </div>
    </AuthGate>
  );
};

export default Register;
